import {
  noWineFilter,
  type BottleFormat,
  type StockState,
  type WindowState,
  type WineFilterCriteria,
  type WineListRow,
  type WineOrder,
  wineFilterIsDefault,
  wineOrderIsActive,
} from "@corklog/shared";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { countryName } from "../countries.js";
import { locale } from "../locale.js";

import { control, resting, Select } from "./controls.js";
import { CountrySelect } from "./CountrySelect.js";
import { describeOrder, WineSorting } from "./WineSorting.js";

/**
 * Finding a bottle in the list: one field to type into, and four narrowings.
 *
 * The search field comes first and is the wide one, because the fifteen-second
 * lookup standing at the cabinet is usually *Hébrart* or *47*, not a
 * combination of dropdowns.
 *
 * **The controls are drawn as controls.** They were underlines on the page's
 * own ground — no border, no surface, nothing but a hairline below the text —
 * on the argument that the bar must stay quiet so the drinking-window band can
 * carry the screen. Quiet was the right aim and an underline was the wrong
 * means: on a flat ground a rule under a word reads as a heading with a rule
 * under it, the placeholder sat where a typed value sits and looked like one,
 * and a narrowing that was set looked exactly like one that was not. Quiet is
 * now carried by size and colour — the fields are the page's surface with one
 * hairline round them, the band is still the only saturated thing on the
 * screen — and the controls are legible as things you can operate.
 *
 * **The narrowings sit in a grid of equal columns** rather than flowing at
 * their content's width. A select is as wide as its longest option, so *Alla
 * typer* and *Finns i källaren* came out at half and double the width of each
 * other and the labels above them landed at five unrelated positions. Equal
 * columns cost nothing and are the difference between a row of controls and a
 * ragged line of them. The grid fits as many as the width allows, so the same
 * markup is two columns on a phone and one row on a desktop.
 *
 * **A narrowing that is set says so in brass.** The interactive colour is the
 * peak of the window ramp and this is the same statement: *this is the one
 * that is doing something*. Without it the only evidence that a filter is on
 * is the count under the bar, which is where a reader looks last.
 *
 * The type, country and format lists are built from the wines actually in the
 * cellar rather than from the enum. A cellar with no Portuguese wine in it
 * should not offer Portugal and then answer with an empty list — the options
 * are a description of what is on the shelves.
 *
 * Format goes further and disappears entirely from a cellar kept in one size,
 * which is most cellars and this one for its first hundred bottles. A control
 * whose every option returns the whole list is not a narrowing, and this bar
 * has too little room to spend any of it on one.
 *
 * The window states are the fixed five, including `unknown`, which is how the
 * wines still missing their four years are found in order to be given them.
 */
const WINDOW_STATES: readonly WindowState[] = [
  "young",
  "ready",
  "peak",
  "past",
  "unknown",
];

/**
 * The three stock views, listed as they were asked for: everything, what is
 * still owned, what is finished.
 *
 * This one is a plain select rather than a `Narrowing`, because it has no
 * *any* option in the sense the others do: showing every wine is one of three
 * answers rather than the absence of an answer, and the empty option those
 * carry would be a fourth state meaning nothing.
 */
const STOCK_STATES: readonly StockState[] = [
  "any",
  "inCellar",
  "outOfCellar",
  // Last, beside the state it narrows: every wine on it is also *slut*.
  "buyAgain",
];

/**
 * The sizes, in size order rather than in the alphabet's — the same order the
 * server lists a wine's own sizes in, so the two cannot read differently.
 */
const FORMATS: readonly BottleFormat[] = [
  "Half",
  "Bottle",
  "Magnum",
  "Jeroboam",
  "Other",
];

/**
 * How a place is written into an option's value: a prefix saying whether it is
 * a whole cabinet or a shelf in one, and a separator no cabinet is named with.
 * Names are free text, so they cannot be told apart from a delimiter that
 * might occur in one.
 */
const CABINET = "c:";
const SHELF = "s:";
const SEP = "\u001f";

function Narrowing({
  id,
  label,
  value,
  onChange,
  options,
  anyLabel,
}: {
  id: string;
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: readonly { value: string; label: string }[];
  anyLabel: string;
}) {
  const set = value !== null;
  return (
    <div>
      <label
        className={`block text-sm ${set ? "text-accent" : "text-muted"}`}
        htmlFor={id}
      >
        {label}
      </label>
      <div className="mt-1">
        <Select
          id={id}
          value={value ?? ""}
          onChange={(next) => onChange(next === "" ? null : next)}
          active={set}
        >
          <option value="">{anyLabel}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}

export function WineFilters({
  wines,
  criteria,
  onChange,
  onReset,
  shown,
  order,
  onOrderChange,
  dated,
}: {
  /** Every wine in the cellar, unfiltered — what the options are read off. */
  wines: readonly Pick<WineListRow, "type" | "countryCode" | "formats" | "standing">[];
  criteria: WineFilterCriteria;
  onChange: (criteria: WineFilterCriteria) => void;
  /** Puts the narrowing back to the list's own. The screen's, not this bar's,
   *  because it also clears *new since last time*, which is not drawn here. */
  onReset: () => void;
  /** How many of them are left after the narrowing. */
  shown: number;
  /**
   * The order the list is read in, drawn under the narrowings and folded away
   * with them. It is not a narrowing — it has its own value and its own place
   * in the address, and *rensa filter* below does not touch it — but on a phone
   * it is part of the same one line, so it is held here.
   */
  order: WineOrder;
  onOrderChange: (order: WineOrder) => void;
  /** Whether the rows say when each wine was added, which decides the order
   *  the list rests in and whether *Tillagd* is offered — see `Wines.tsx`. */
  dated: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const searchId = useId();
  const stockId = useId();
  const typeId = useId();
  const countryId = useId();
  const formatId = useId();
  const windowId = useId();
  const placeId = useId();

  // The labels are the interface's own words, so they sort in its language.
  const collator = new Intl.Collator(locale());

  const types = [...new Set(wines.map((wine) => wine.type))]
    .map((type) => ({ value: type, label: t(`wineType.${type}`) }))
    .sort((a, b) => collator.compare(a.label, b.label));

  const countries = [...new Set(wines.map((wine) => wine.countryCode))]
    .map((code) => ({ value: code, label: countryName(code) }))
    .sort((a, b) => collator.compare(a.label, b.label));

  // Cabinets and the shelves in them, read off where bottles stand now, so a
  // cabinet that is empty is not offered. One control rather than two because a
  // shelf means nothing outside its cabinet — most cabinets have a shelf 5 —
  // so the shelves are listed under the cabinet they belong to, and a cabinet
  // can be chosen whole. Numeric collation, so shelf 2 comes before shelf 10.
  const numeric = new Intl.Collator(locale(), { numeric: true });
  const shelvesBy = new Map<string, Set<string>>();
  for (const wine of wines) {
    for (const place of wine.standing) {
      if (place.storageName == null) continue;
      const shelves = shelvesBy.get(place.storageName) ?? new Set<string>();
      if (place.shelfName != null) shelves.add(place.shelfName);
      shelvesBy.set(place.storageName, shelves);
    }
  }
  // The place being asked for is always offered, as the size is: a link to a
  // shelf that is now empty should read as that shelf, not as "all places".
  if (criteria.storage !== null) {
    const shelves = shelvesBy.get(criteria.storage) ?? new Set<string>();
    if (criteria.shelf !== null) shelves.add(criteria.shelf);
    shelvesBy.set(criteria.storage, shelves);
  }
  const placeValue =
    criteria.storage === null
      ? ""
      : criteria.shelf === null
        ? `${CABINET}${criteria.storage}`
        : `${SHELF}${criteria.storage}${SEP}${criteria.shelf}`;
  const placeLabel =
    criteria.storage === null
      ? ""
      : criteria.shelf === null
        ? criteria.storage
        : `${criteria.storage} · ${t("wines.filter.shelf")} ${criteria.shelf}`;

  // Sizes, kept in size order rather than sorted by their Swedish names, which
  // would put the half bottle between the magnum and the jeroboam.
  //
  // The size being asked for is always among them, even when the cellar holds
  // none of it. A select cannot show a value it has no option for, so leaving
  // it out would make the control read "Alla format" over an empty list — the
  // one narrowing at work, saying it is not narrowing anything.
  const held = new Set(wines.flatMap((wine) => wine.formats));
  if (criteria.format !== null) held.add(criteria.format);
  const formats = FORMATS.filter((format) => held.has(format)).map((format) => ({
    value: format,
    label: t(`format.${format}`),
  }));

  // A cellar in one size gets no size control at all: with one option the
  // question has one answer, and this bar has no room for a narrowing that
  // narrows nothing. A size that is being asked for makes it two, which is
  // what puts the control back in front of a link asking for magnums in a
  // cellar that has none.
  const sizesMatter = formats.length > 1;

  // The bar speaks when rows are actually being withheld, which is not the
  // same as "a narrowing is set". The list opens with one — it hides the wines
  // that are finished — and in a cellar where nothing is finished yet that
  // filter hides nothing, so "Visar 12 av 12 viner" beside a way out of a
  // filter that took nothing away would be noise claiming to be information.
  const hiding = shown < wines.length;

  // The stock view is the one narrowing with no empty option, so *set* has to
  // be defined rather than read off a null. It is marked whenever it is not
  // *every wine* — including on arrival, where the list opens holding back
  // what is finished. That is the state the mark exists for: sixteen wines are
  // missing from the screen before anybody has touched a control, and this is
  // what says where they went.
  const stockNarrowed = criteria.stock !== noWineFilter.stock;

  // What the folded section says about itself: every choice that is doing
  // something, and the order, which always has a value and is always said —
  // a phone showing a list of wines out of number order with nothing on screen
  // to say why would be a list that looks shuffled. Each part carries its own
  // brass, by the same rule as the controls it stands for.
  const summary: { text: string; active: boolean }[] = [
    { text: t(`wines.filter.stock.${criteria.stock}`), active: stockNarrowed },
    ...(criteria.type === null
      ? []
      : [{ text: t(`wineType.${criteria.type}`), active: true }]),
    ...(criteria.countryCode === null
      ? []
      : [{ text: countryName(criteria.countryCode), active: true }]),
    ...(criteria.storage === null ? [] : [{ text: placeLabel, active: true }]),
    ...(criteria.format === null
      ? []
      : [{ text: t(`format.${criteria.format}`), active: true }]),
    ...(criteria.window === null
      ? []
      : [{ text: t(`window.state.${criteria.window}`), active: true }]),
    { text: describeOrder(order, t), active: wineOrderIsActive(order, dated) },
  ];

  // A search landmark, so the one control that matters standing at the cabinet
  // can be jumped to directly, and so "Typ" here is distinguishable from the
  // "Typ" on the registration form above it — for a screen reader as much as
  // for the test. A div carrying the role rather than <search>, which jsdom
  // does not know yet and warns about on every render.
  return (
    <div
      role="search"
      aria-label={t("wines.filter.search")}
      className="border-line mt-8 border-t pt-6"
    >
      {/* Capped rather than full width. The field is for *Hébrart* or *47*,
          and a text box the width of the page promises a sentence. */}
      <div className="max-w-md">
        <label className="text-muted block text-sm" htmlFor={searchId}>
          {t("wines.filter.search")}
        </label>
        <input
          id={searchId}
          type="search"
          value={criteria.query}
          placeholder={t("wines.filter.searchHint")}
          onChange={(event) => onChange({ ...criteria, query: event.target.value })}
          className={`${control} ${resting} placeholder:text-muted/70 mt-1`}
        />
      </div>

      {/* The narrowings and the order, folded into one line on a phone.

          Six controls in two columns are three rows of selects, and on a
          phone that pushed the first wine below the fold — the list is what
          the screen is for, and the controls are reached for far less often
          than they were standing in the way. So on a narrow screen they close
          into a single line saying what is chosen, and one tap opens them.

          **Only on a narrow screen.** On a desktop the six fit in two short
          rows beside plenty of room, and a fold there would hide what costs
          nothing to show. The fold is CSS rather than a render condition for
          the same reason: the controls exist at every width, and the width
          alone decides whether they are drawn.

          The search field stays outside it, because it is the one control
          used standing at the cabinet, and so does the count, because it is
          what says rows are being held back. */}
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((shown) => !shown)}
        className="border-line mt-4 flex w-full items-center gap-3 border-y py-3 text-left sm:hidden"
      >
        <span className="min-w-0 flex-1">
          <span className="text-muted block text-sm">{t("wines.filter.section")}</span>
          {/* One line, cut off with an ellipsis when it runs out — the whole
              of it is one tap away, and a summary that wrapped onto three
              lines would be the space this was built to give back. */}
          <span className="mt-0.5 block truncate text-sm">
            {summary.map((part, at) => (
              <span key={part.text}>
                {at > 0 && <span className="text-muted"> · </span>}
                <span className={part.active ? "text-accent" : "text-ink"}>
                  {part.text}
                </span>
              </span>
            ))}
          </span>
        </span>
        {/* The select's chevron, drawn here in its own box: it points down
            while the section is closed and up once it is open. */}
        <svg
          viewBox="0 0 10 6"
          aria-hidden="true"
          className={`text-muted h-1.5 w-2.5 shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        >
          <path
            d="M1 1l4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div id={panelId} className={`${open ? "block" : "hidden"} sm:block`}>
        {/* As many equal columns as the width allows: two on a phone, one row on
          a desktop, and never a column narrower than its widest option. */}
        <div className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(9.5rem,1fr))] gap-4">
          {/* First, because it decides which list the others narrow. */}
          <div>
            <label
              className={`block text-sm ${stockNarrowed ? "text-accent" : "text-muted"}`}
              htmlFor={stockId}
            >
              {t("wines.stock")}
            </label>
            <div className="mt-1">
              <Select
                id={stockId}
                value={criteria.stock}
                onChange={(next) => onChange({ ...criteria, stock: next as StockState })}
                active={stockNarrowed}
              >
                {STOCK_STATES.map((state) => (
                  <option key={state} value={state}>
                    {t(`wines.filter.stock.${state}`)}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <Narrowing
            id={typeId}
            label={t("wines.type")}
            value={criteria.type}
            onChange={(value) =>
              onChange({ ...criteria, type: value as WineListRow["type"] | null })
            }
            options={types}
            anyLabel={t("wines.filter.anyType")}
          />
          {/* The one narrowing that is not a Narrowing: a country carries a flag
            and an <option> cannot hold one. Same label, same brass when it is
            set — only the list underneath differs. */}
          <div>
            <label
              className={`block text-sm ${
                criteria.countryCode !== null ? "text-accent" : "text-muted"
              }`}
              htmlFor={countryId}
            >
              {t("wines.country")}
            </label>
            <div className="mt-1">
              <CountrySelect
                id={countryId}
                value={criteria.countryCode}
                onChange={(value) => onChange({ ...criteria, countryCode: value })}
                options={countries.map((country) => country.value)}
                anyLabel={t("wines.filter.anyCountry")}
              />
            </div>
          </div>
          {shelvesBy.size > 0 && (
            <div>
              <label
                className={`block text-sm ${
                  criteria.storage !== null ? "text-accent" : "text-muted"
                }`}
                htmlFor={placeId}
              >
                {t("wines.filter.place")}
              </label>
              <div className="mt-1">
                <Select
                  id={placeId}
                  value={placeValue}
                  onChange={(next) => {
                    if (next.startsWith(CABINET)) {
                      onChange({
                        ...criteria,
                        storage: next.slice(CABINET.length),
                        shelf: null,
                      });
                    } else if (next.startsWith(SHELF)) {
                      const [storage = "", shelf = ""] = next
                        .slice(SHELF.length)
                        .split(SEP);
                      onChange({ ...criteria, storage, shelf });
                    } else {
                      onChange({ ...criteria, storage: null, shelf: null });
                    }
                  }}
                  active={criteria.storage !== null}
                >
                  <option value="">{t("wines.filter.anyPlace")}</option>
                  {[...shelvesBy.keys()].sort(numeric.compare).map((storage) => (
                    <optgroup key={storage} label={storage}>
                      <option value={`${CABINET}${storage}`}>
                        {t("wines.filter.wholeCabinet")}
                      </option>
                      {[...(shelvesBy.get(storage) ?? [])]
                        .sort(numeric.compare)
                        .map((shelf) => (
                          <option key={shelf} value={`${SHELF}${storage}${SEP}${shelf}`}>
                            {t("wines.filter.shelf")} {shelf}
                          </option>
                        ))}
                    </optgroup>
                  ))}
                </Select>
              </div>
            </div>
          )}
          {sizesMatter && (
            <Narrowing
              id={formatId}
              label={t("wines.format")}
              value={criteria.format}
              onChange={(value) =>
                onChange({ ...criteria, format: value as BottleFormat | null })
              }
              options={formats}
              anyLabel={t("wines.filter.anyFormat")}
            />
          )}
          <Narrowing
            id={windowId}
            label={t("wines.window")}
            value={criteria.window}
            onChange={(value) =>
              onChange({ ...criteria, window: value as WindowState | null })
            }
            options={WINDOW_STATES.map((state) => ({
              value: state,
              label: t(`window.state.${state}`),
            }))}
            anyLabel={t("wines.filter.anyWindow")}
          />
        </div>

        <WineSorting order={order} onChange={onOrderChange} dated={dated} />
      </div>

      {/* The count, and the way back out, side by side on the hairline the
          list begins at. Announced politely rather than asserted, so a screen
          reader hears how many wines are left after the typing stops instead
          of after every keystroke.

          The way out sits beside the number it undoes rather than at the far
          edge of the page: they are one sentence — *12 of 121, clear the
          filter* — and a button an armslength away from its own subject reads
          as a second, unrelated control. */}
      <div className="mt-5 flex items-baseline gap-4">
        <p aria-live="polite" className="text-muted text-sm">
          {hiding
            ? t("wines.filter.count", { shown, total: wines.length })
            : t("wines.filter.total", { count: wines.length })}
        </p>
        {/* Back to what the list opens with — *Finns i källaren* and nothing
            else — rather than to every wine, on the owner's word of
            2026-10-07. So it is drawn only when something beyond that is
            chosen; on the resting list it would be a button that does
            nothing, and the finished wines are under *Lager*. */}
        {hiding && !wineFilterIsDefault(criteria) && (
          <button
            type="button"
            onClick={onReset}
            className="text-accent text-sm underline-offset-4 hover:underline"
          >
            {t("wines.filter.reset")}
          </button>
        )}
      </div>
    </div>
  );
}
